import React, { useState, useEffect } from 'react'
import Calendar from 'react-calendar'
import {
  CalendarIcon,
  ChevronLeftIcon,
  ChevronRightIcon
} from '@components/kit/icons'
import { NutritionDayStatus } from '@store/Nutrition/types'
import './nutrition-calendar.css'

export interface DateStatus {
  [dateString: string]: NutritionDayStatus[]
}

const getStatusClassName = (status: NutritionDayStatus): string => {
  if (status == NutritionDayStatus.NO_MEAL) {
    return 'nutrition-calendar-dot--no-meal'
  } else {
    return 'nutrition-calendar-dot--active-day'
  }
  /*
  switch (status) {
    case NutritionDayStatus.EXCEEDED_ALL_GOALS:
      return 'nutrition-calendar-dot--exceeded'
    case NutritionDayStatus.ALL_GOALS:
      return 'nutrition-calendar-dot--all-goals'
    case NutritionDayStatus.PARTIAL_GOAL:
      return 'nutrition-calendar-dot--partial'
    case NutritionDayStatus.NO_GOAL:
      return 'nutrition-calendar-dot--no-goal'
    case NutritionDayStatus.NO_MEAL:
    default:
      return 'nutrition-calendar-dot--no-meal'
  }
  */
}

interface NutritionCalendarProps {
  onDateSelect?: (date: Date) => void
  onMonthChange?: (date: Date) => void
  dateStatus?: DateStatus
  date?: Date | null
}

function NutritionCalendar({
  onDateSelect,
  onMonthChange,
  dateStatus = {},
  date
}: NutritionCalendarProps) {
  const [selectedDate, setSelectedDate] = useState<Date | null>(date || null)
  const [statusData, setStatusData] = useState<DateStatus>(dateStatus)

  useEffect(() => {
    if (date !== undefined) {
      setSelectedDate(date)
    }
  }, [date])

  useEffect(() => {
    setStatusData(dateStatus)
  }, [dateStatus])

  const handleDateSelect = (date: Date) => {
    setSelectedDate(date)
    if (onDateSelect) {
      onDateSelect(date)
    }
  }

  const formatDateKey = (date: Date): string => {
    const year = date.getFullYear()
    const month = String(date.getMonth() + 1).padStart(2, '0')
    const day = String(date.getDate()).padStart(2, '0')
    return `${year}${month}${day}`
  }

  const tileContent = ({ date, view }: { date: Date; view: string }) => {
    if (view !== 'month') return null

    const dateKey = formatDateKey(date)
    const statuses = statusData[dateKey]

    if (!statuses || statuses.length === 0) return null

    return (
      <div className='nutrition-calendar-dots nutrition-calendar-dots--top'>
        {statuses.slice(0, 3).map((status, index) => {
          const className = getStatusClassName(status)
          return (
            <div
              key={index}
              className={`nutrition-calendar-dot nutrition-calendar-dot--big ${className}`}
            />
          )
        })}
      </div>
    )
  }

  const tileClassName = ({ date, view }: { date: Date; view: string }) => {
    if (view !== 'month') return null

    const dateKey = formatDateKey(date)
    const statuses = statusData[dateKey]

    if (statuses && statuses.length > 0) {
      return 'nutrition-calendar-tile--has-dots'
    }

    return null
  }

  const formatShortWeekday = (_locale: string | undefined, date: Date) => {
    const map = ['Su', 'Mo', 'Tu', 'We', 'Th', 'Fr', 'Sa']
    return map[date.getDay()]
  }

  const navigationLabel = ({ date }: { date: Date }) => {
    const month = date.toLocaleString('en-US', { month: 'long' })
    const year = date.getFullYear()
    return (
      <div className='nutrition-calendar-nav-label'>
        <CalendarIcon />
        <span>{` ${month} ${year}`}</span>
      </div>
    )
  }

  return (
    <div className='nutrition-calendar-wrapper'>
      <Calendar
        onClickDay={handleDateSelect}
        onActiveStartDateChange={({ activeStartDate }) => {
          if (activeStartDate && onMonthChange) {
            onMonthChange(activeStartDate)
          }
        }}
        value={selectedDate}
        tileContent={tileContent}
        tileClassName={tileClassName}
        locale='en-US'
        prev2Label={null}
        next2Label={null}
        prevLabel={
          <ChevronLeftIcon width={6} height={12} fill='walrusNeutral700' />
        }
        nextLabel={
          <ChevronRightIcon width={6} height={12} fill='walrusNeutral700' />
        }
        formatShortWeekday={formatShortWeekday}
        navigationLabel={navigationLabel}
      />
    </div>
  )
}

export default NutritionCalendar
